import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient, useQuery } from "react-query";
import api from "../services/api";
import { recruitingAPI } from "../services/recruitingApi";
import TagInput from "./TagInput";
import TagSuggestionsModal from "./TagSuggestionsModal";
import { CandidateStatusBadge, STATUS_CONFIG } from "./CandidateStatusControl";

interface CandidateData {
  id?: string | number;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  location?: string;
  linkedin_url?: string;
  main_skill?: string;
  seniority?: string;
  role_type?: string;
  expected_rate?: string | number;
  notice_period?: string;
  english_level?: string;
  decision?: string;
  status?: string;
  resume_url?: string;
  summary?: string;
  tags?: string | string[];
  source?: string;
  responsible_recruiter_id?: string | number;
  skills_data?: string | Record<string, number>;
}

interface CandidateFormProps {
  candidate?: CandidateData | null;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: (candidateId: string | number) => void;
}

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  location: string;
  linkedin_url: string;
  main_skill: string;
  seniority: string;
  role_type: string;
  expected_rate: string;
  notice_period: string;
  english_level: string;
  decision: string;
  resume_url: string;
  summary: string;
  tags: string;
  source: string;
  responsible_recruiter_id: string;
  skills: string[];
  [key: string]: string | string[];
}

interface FormErrors {
  [key: string]: string;
}

interface User {
  id: number;
  username: string;
  email: string;
}

interface UsersResponse {
  users?: User[];
}

interface GenerateTextResponse {
  success?: boolean;
  result: {
    success: boolean;
    content: string;
    usage?: Record<string, number>;
  };
}

interface TagsResponse {
  success?: boolean;
  tags?: { tag: string; reason?: string }[];
}

const CandidateForm: React.FC<CandidateFormProps> = ({
  candidate,
  onSave,
  onCancel,
  onDelete,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<FormData>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    location: "",
    linkedin_url: "",
    main_skill: "",
    seniority: "",
    role_type: "",
    expected_rate: "",
    notice_period: "",
    english_level: "",
    decision: "undecided",
    status: "preparation",
    resume_url: "",
    summary: "",
    tags: "",
    source: "",
    responsible_recruiter_id: "",
    skills: [],
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isGeneratingTags, setIsGeneratingTags] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState<
    { tag: string; reason?: string }[]
  >([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  // Fetch recruiter users
  const { data: recruitersData } = useQuery<UsersResponse>(
    ["users", "recruiter"],
    () =>
      recruitingAPI.getUsersByRole(
        "recruiter",
      ) as unknown as Promise<UsersResponse>,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  const recruiters = recruitersData?.users || [];

  // Initialize form data when candidate prop changes
  useEffect(() => {
    if (candidate) {
      setFormData({
        first_name: candidate.first_name || "",
        last_name: candidate.last_name || "",
        email: candidate.email || "",
        phone: candidate.phone || "",
        location: candidate.location || "",
        linkedin_url: candidate.linkedin_url || "",
        main_skill: candidate.main_skill || "",
        seniority: candidate.seniority || "",
        role_type: candidate.role_type || "",
        expected_rate: candidate.expected_rate?.toString() || "",
        notice_period: candidate.notice_period || "",
        english_level: candidate.english_level || "",
        decision: candidate.decision || "undecided",
        status: candidate.status || "preparation",
        resume_url: candidate.resume_url || "",
        summary: candidate.summary || "",
        tags: Array.isArray(candidate.tags) ? candidate.tags.join(", ") : "",
        source: candidate.source || "",
        responsible_recruiter_id:
          candidate.responsible_recruiter_id?.toString() || "",
        skills: (() => {
          // Parse skills_data if it exists, otherwise use empty array
          if (candidate.skills_data) {
            try {
              const skillsData =
                typeof candidate.skills_data === "string"
                  ? JSON.parse(candidate.skills_data)
                  : candidate.skills_data;
              // Extract skill names from skills_data object if it's an object with skill values
              if (
                typeof skillsData === "object" &&
                skillsData !== null &&
                !Array.isArray(skillsData)
              ) {
                return Object.keys(skillsData);
              }
              // If it's already an array of skill names, return it
              if (Array.isArray(skillsData)) {
                return skillsData;
              }
            } catch (e) {
              console.error("Error parsing skills_data:", e);
            }
          }
          return [];
        })(),
      });
    }
  }, [candidate]);

  // Create/Update candidate mutation
  const saveCandidateMutation = useMutation<
    unknown,
    Error,
    Record<string, unknown>
  >(
    async (data) => {
      if (candidate) {
        return await api.recruiting.updateCandidate(
          candidate.id as string | number,
          data,
        );
      } else {
        return await api.recruiting.createCandidate(data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("candidates");
        if (candidate) {
          queryClient.invalidateQueries(["candidate", candidate.id]);
        }
        onSave();
      },
      onError: (error: Error) => {
        console.error("Error saving candidate:", error);
        setErrors({ general: t("candidates.form.validation.saveFailed") });
      },
    },
  );

  // Generate AI summary mutation
  const generateSummaryMutation = useMutation<string, Error>(
    async () => {
      // Create a temporary candidate object with current form data
      const tempCandidate = {
        ...formData,
        tags: formData.tags
          ? formData.tags
              .split(",")
              .map((tag) => tag.trim())
              .filter(Boolean)
          : [],
      };

      // Use OpenAI API to generate summary
      const prompt = `Please create a professional summary for this candidate based on their profile:

Name: ${tempCandidate.first_name} ${tempCandidate.last_name}
Email: ${tempCandidate.email || "Not provided"}
Location: ${tempCandidate.location || "Not provided"}
Main Skill: ${tempCandidate.main_skill || "Not specified"}
Seniority: ${tempCandidate.seniority || "Not specified"}
Role Type: ${tempCandidate.role_type || "Not specified"}
Expected Rate: ${tempCandidate.expected_rate ? `$${tempCandidate.expected_rate}/day` : "Not specified"}
Notice Period: ${tempCandidate.notice_period || "Not specified"}
English Level: ${tempCandidate.english_level || "Not specified"}
Source: ${tempCandidate.source || "Not specified"}
Tags: ${tempCandidate.tags.join(", ") || "None"}
LinkedIn: ${tempCandidate.linkedin_url || "Not provided"}

Please provide a concise, professional summary (2-3 sentences) that highlights their key qualifications, experience level, and availability. Focus on what makes them a strong candidate.`;
      const response = (await api.openai.generateText(prompt, {
        max_tokens: 200,
        temperature: 0.3,
      })) as GenerateTextResponse;

      return response.result.content;
    },
    {
      onSuccess: (summary) => {
        setFormData((prev) => ({ ...prev, summary }));
        setIsGeneratingSummary(false);
      },
      onError: (error: Error) => {
        console.error("Error generating summary:", error);
        setIsGeneratingSummary(false);
        setErrors({
          summary: t("candidates.form.validation.summaryFailed"),
        });
      },
    },
  );

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleGenerateSummary = async () => {
    setIsGeneratingSummary(true);
    setErrors((prev) => ({ ...prev, summary: "" }));
    generateSummaryMutation.mutate();
  };

  const handleGenerateTags = async () => {
    setIsGeneratingTags(true);
    setErrors((prev) => ({ ...prev, tags: "" }));

    try {
      const existingTags = formData.tags
        ? formData.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean)
        : [];

      const content = {
        name: `${formData.first_name} ${formData.last_name}`,
        first_name: formData.first_name,
        last_name: formData.last_name,
        main_skill: formData.main_skill,
        seniority: formData.seniority,
        role_type: formData.role_type,
        location: formData.location,
        summary: formData.summary,
        skills: formData.skills,
        expected_rate: formData.expected_rate,
        notice_period: formData.notice_period,
        english_level: formData.english_level,
      };

      const response = (await api.openai.generateTags(
        JSON.stringify(content),
        "candidate",
        existingTags,
      )) as TagsResponse;

      if (response.success && response.tags && response.tags.length > 0) {
        setTagSuggestions(response.tags);
        setShowTagSuggestions(true);
      } else {
        setErrors((prev) => ({
          ...prev,
          tags: t("candidates.form.validation.noTagSuggestions"),
        }));
      }
    } catch (error) {
      console.error("Error generating tags:", error);
      setErrors((prev) => ({
        ...prev,
        tags: t("candidates.form.validation.tagsFailed"),
      }));
    } finally {
      setIsGeneratingTags(false);
    }
  };

  const handleAddSuggestedTags = (selectedTags: string[]) => {
    const currentTags = formData.tags
      ? formData.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean)
      : [];

    const newTags = [...currentTags, ...selectedTags];
    const uniqueTags = [...new Set(newTags)];

    setFormData((prev) => ({ ...prev, tags: uniqueTags.join(", ") }));
    setTagSuggestions([]);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = t("candidates.form.validation.firstNameRequired");
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = t("candidates.form.validation.lastNameRequired");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("candidates.form.validation.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("candidates.form.validation.emailInvalid");
    }

    if (
      formData.expected_rate &&
      Number.isNaN(Number.parseFloat(formData.expected_rate))
    ) {
      newErrors.expected_rate = t(
        "candidates.form.validation.expectedRateNumber",
      );
    }

    if (formData.linkedin_url && !formData.linkedin_url.startsWith("http")) {
      newErrors.linkedin_url = t(
        "candidates.form.validation.linkedInUrlInvalid",
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare data for API
    const submitData: Record<string, unknown> = {
      ...formData,
      expected_rate: formData.expected_rate
        ? Number.parseFloat(formData.expected_rate)
        : null,
      responsible_recruiter_id: formData.responsible_recruiter_id
        ? Number.parseInt(formData.responsible_recruiter_id, 10)
        : null,
      tags: formData.tags
        ? formData.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean)
        : [],
      // Convert skills array to skills_data JSON format
      skills_data:
        formData.skills && formData.skills.length > 0
          ? JSON.stringify(
              formData.skills.reduce((acc: Record<string, number>, skill) => {
                acc[skill] = 50; // Default skill level
                return acc;
              }, {}),
            )
          : null,
    };

    // Remove skills from submitData as it's not a direct field
    delete submitData.skills;

    saveCandidateMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (
      candidate &&
      candidate.id &&
      window.confirm(
        t("candidates.form.confirmDelete", {
          name: `${candidate.first_name} ${candidate.last_name}`,
        }),
      )
    ) {
      onDelete?.(candidate.id);
    }
  };

  return (
    <div className="candidate-form">
      <div className="form-header">
        <h2>
          {candidate
            ? t("candidates.form.titleEdit")
            : t("candidates.form.titleNew")}
        </h2>
        <div className="form-actions">
          {candidate && (
            <button
              type="button"
              onClick={handleDelete}
              className="btn btn-delete"
            >
              {t("common:actions.delete")}
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
          >
            {t("common:actions.cancel")}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="form-content">
        {errors.general && (
          <div className="error-message general-error">{errors.general}</div>
        )}

        {/* Personal Information */}
        <div className="form-section">
          <h3>{t("candidates.form.sections.personal")}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">
                {t("candidates.form.fields.firstName")} *
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className={errors.first_name ? "error" : ""}
                required
              />
              {errors.first_name && (
                <span className="error-text">{errors.first_name}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="last_name">
                {t("candidates.form.fields.lastName")} *
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className={errors.last_name ? "error" : ""}
                required
              />
              {errors.last_name && (
                <span className="error-text">{errors.last_name}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">
                {t("candidates.form.fields.email")} *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? "error" : ""}
                required
              />
              {errors.email && (
                <span className="error-text">{errors.email}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="phone">{t("candidates.form.fields.phone")}</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="location">
                {t("candidates.form.fields.location")}
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder={t("candidates.form.placeholders.location")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="linkedin_url">
                {t("candidates.form.fields.linkedIn")}
              </label>
              <input
                type="url"
                id="linkedin_url"
                name="linkedin_url"
                value={formData.linkedin_url}
                onChange={handleInputChange}
                className={errors.linkedin_url ? "error" : ""}
                placeholder={t("candidates.form.placeholders.linkedIn")}
              />
              {errors.linkedin_url && (
                <span className="error-text">{errors.linkedin_url}</span>
              )}
            </div>
          </div>
        </div>

        {/* Professional Information */}
        <div className="form-section">
          <h3>{t("candidates.form.sections.professional")}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="main_skill">
                {t("candidates.form.fields.mainSkill")}
              </label>
              <select
                id="main_skill"
                name="main_skill"
                value={formData.main_skill}
                onChange={handleInputChange}
              >
                <option value="">
                  {t("candidates.form.selects.selectSkill")}
                </option>
                <option value="Azure">Azure</option>
                <option value="AWS">AWS</option>
                <option value="GCP">GCP</option>
                <option value="Kubernetes">Kubernetes</option>
                <option value="Terraform">Terraform</option>
                <option value="Docker">Docker</option>
                <option value="Python">Python</option>
                <option value="JavaScript">JavaScript</option>
                <option value="React">React</option>
                <option value="Node.js">Node.js</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="seniority">
                {t("candidates.form.fields.seniority")}
              </label>
              <select
                id="seniority"
                name="seniority"
                value={formData.seniority}
                onChange={handleInputChange}
              >
                <option value="">
                  {t("candidates.form.selects.selectSeniority")}
                </option>
                <option value="Junior">
                  {t("candidates.seniority.junior")}
                </option>
                <option value="Mid">{t("candidates.seniority.mid")}</option>
                <option value="Senior">
                  {t("candidates.seniority.senior")}
                </option>
                <option value="Lead">{t("candidates.seniority.lead")}</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="role_type">
                {t("candidates.form.fields.roleType")}
              </label>
              <input
                type="text"
                id="role_type"
                name="role_type"
                value={formData.role_type}
                onChange={handleInputChange}
                placeholder={t("candidates.form.placeholders.roleType")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="expected_rate">
                {t("candidates.form.fields.expectedRate")}
              </label>
              <input
                type="number"
                id="expected_rate"
                name="expected_rate"
                value={formData.expected_rate}
                onChange={handleInputChange}
                className={errors.expected_rate ? "error" : ""}
                min="0"
                step="0.01"
              />
              {errors.expected_rate && (
                <span className="error-text">{errors.expected_rate}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="skills">
                {t("candidates.form.fields.skills")}
              </label>
              <TagInput
                value={formData.skills}
                onChange={(skills: string[]) =>
                  setFormData((prev) => ({ ...prev, skills }))
                }
                placeholder={t("candidates.form.placeholders.skills")}
              />
              <small>{t("candidates.form.hints.skills")}</small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="notice_period">
                {t("candidates.form.fields.noticePeriod")}
              </label>
              <select
                id="notice_period"
                name="notice_period"
                value={formData.notice_period}
                onChange={handleInputChange}
              >
                <option value="">
                  {t("candidates.form.selects.selectNoticePeriod")}
                </option>
                <option value="Immediate">
                  {t("candidates.noticePeriod.immediate")}
                </option>
                <option value="1 week">
                  {t("candidates.noticePeriod.oneWeek")}
                </option>
                <option value="2 weeks">
                  {t("candidates.noticePeriod.twoWeeks")}
                </option>
                <option value="1 month">
                  {t("candidates.noticePeriod.oneMonth")}
                </option>
                <option value="2 months">
                  {t("candidates.noticePeriod.twoMonths")}
                </option>
                <option value="3 months">
                  {t("candidates.noticePeriod.threeMonths")}
                </option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="english_level">
                {t("candidates.form.fields.englishLevel")}
              </label>
              <select
                id="english_level"
                name="english_level"
                value={formData.english_level}
                onChange={handleInputChange}
              >
                <option value="">
                  {t("candidates.form.selects.selectEnglishLevel")}
                </option>
                <option value="A1">{t("candidates.englishLevel.a1")}</option>
                <option value="A2">{t("candidates.englishLevel.a2")}</option>
                <option value="B1">{t("candidates.englishLevel.b1")}</option>
                <option value="B2">{t("candidates.englishLevel.b2")}</option>
                <option value="C1">{t("candidates.englishLevel.c1")}</option>
                <option value="C2">{t("candidates.englishLevel.c2")}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Status & Source */}
        <div className="form-section">
          <h3>{t("candidates.form.sections.status")}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">
                {t("candidates.form.fields.status")}
              </label>
              {candidate?.id ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    minHeight: "38px",
                  }}
                >
                  <CandidateStatusBadge
                    status={candidate?.status || "preparation"}
                    size="medium"
                  />
                  <span style={{ fontSize: "12px", color: "#6b7280" }}>
                    {t("candidates.form.hints.statusNote")}
                  </span>
                </div>
              ) : (
                <select
                  id="status"
                  name="status"
                  value={formData.status || "preparation"}
                  onChange={handleInputChange}
                >
                  {Object.entries(STATUS_CONFIG).map(([value, config]) => {
                    const currentStatus = formData.status || "preparation";
                    // For new candidates, only preparation (current) and tech_check (next step) are valid
                    const isEnabled =
                      value === currentStatus || value === "tech_check";
                    return (
                      <option key={value} value={value} disabled={!isEnabled}>
                        {config.label}
                      </option>
                    );
                  })}
                </select>
              )}
              {/* Hidden decision field for backward compatibility */}
              <input type="hidden" name="decision" value={formData.decision} />
            </div>
            <div className="form-group">
              <label htmlFor="source">
                {t("candidates.form.fields.source")}
              </label>
              <select
                id="source"
                name="source"
                value={formData.source}
                onChange={handleInputChange}
              >
                <option value="">
                  {t("candidates.form.selects.selectSource")}
                </option>
                <option value="linkedin">
                  {t("candidates.source.linkedin")}
                </option>
                <option value="job-board">
                  {t("candidates.source.jobBoard")}
                </option>
                <option value="referral">
                  {t("candidates.source.referral")}
                </option>
                <option value="recruiter">
                  {t("candidates.source.recruiter")}
                </option>
                <option value="direct-application">
                  {t("candidates.source.directApplication")}
                </option>
                <option value="other">{t("candidates.source.other")}</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="responsible_recruiter_id">
              {t("candidates.form.fields.responsibleRecruiter")}
            </label>
            <select
              id="responsible_recruiter_id"
              name="responsible_recruiter_id"
              value={formData.responsible_recruiter_id?.toString() || ""}
              onChange={handleInputChange}
            >
              <option value="">
                {t("candidates.form.selects.noResponsibleRecruiter")}
              </option>
              {recruiters.map((user) => (
                <option key={user.id} value={user.id.toString()}>
                  {user.username} ({user.email})
                </option>
              ))}
            </select>
            <small>{t("candidates.form.hints.responsibleRecruiter")}</small>
          </div>
        </div>

        {/* Additional Information */}
        <div className="form-section">
          <h3>{t("candidates.form.sections.additional")}</h3>
          <div className="form-group">
            <label htmlFor="resume_url">
              {t("candidates.form.fields.resumeUrl")}
            </label>
            <input
              type="url"
              id="resume_url"
              name="resume_url"
              value={formData.resume_url}
              onChange={handleInputChange}
              placeholder={t("candidates.form.placeholders.resumeUrl")}
            />
          </div>

          <div className="form-group">
            <label htmlFor="tags">{t("candidates.form.fields.tags")}</label>
            <div
              style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
            >
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder={t("candidates.form.placeholders.tags")}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={handleGenerateTags}
                disabled={isGeneratingTags}
                className="btn btn-outline"
                style={{ whiteSpace: "nowrap" }}
              >
                {isGeneratingTags
                  ? t("candidates.form.buttons.generating")
                  : t("candidates.form.buttons.generateAiTags")}
              </button>
            </div>
            {errors.tags && <span className="error-text">{errors.tags}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="summary">
              {t("candidates.form.fields.summary")}
            </label>
            <div className="summary-section">
              <textarea
                id="summary"
                name="summary"
                value={formData.summary}
                onChange={handleInputChange}
                rows={4}
                placeholder={t("candidates.form.placeholders.summary")}
              />
              <button
                type="button"
                onClick={handleGenerateSummary}
                disabled={isGeneratingSummary}
                className="btn btn-outline generate-summary-btn"
              >
                {isGeneratingSummary
                  ? t("candidates.form.buttons.generating")
                  : t("candidates.form.buttons.generateAiSummary")}
              </button>
            </div>
            {errors.summary && (
              <span className="error-text">{errors.summary}</span>
            )}
          </div>
        </div>

        <div className="form-footer">
          <button
            type="submit"
            disabled={saveCandidateMutation.isLoading}
            className="btn-primary"
          >
            {saveCandidateMutation.isLoading && t("common:actions.saving")}
            {!saveCandidateMutation.isLoading &&
              candidate &&
              t("candidates.form.buttons.updateCandidate")}
            {!saveCandidateMutation.isLoading &&
              !candidate &&
              t("candidates.form.buttons.createCandidate")}
          </button>
        </div>
      </form>

      <TagSuggestionsModal
        isOpen={showTagSuggestions}
        onClose={() => setShowTagSuggestions(false)}
        suggestions={tagSuggestions}
        onAddTags={handleAddSuggestedTags}
        isLoading={isGeneratingTags}
      />
    </div>
  );
};

export default CandidateForm;
